import styled from 'threads5/styles/styled';
import { colors } from 'src/components-v2/theme';
import { CmsThemes } from 'src/lib/constants';
import {
  DesignSystemFontStyleRegistry,
  getResponsiveCssFontStyles,
  LegacyDesignSystemFontStyleMap,
  type TDesignSystemFontStyle,
} from 'src/lib/design-system/typography';

interface CmsTheme {
  cmsTheme?: 'web 2.0' | 'web 3.0';
}

// *********  Homepage styles **********
const DefaultStyles = {
  fontWeight: '800',
  backgroundColor: 'inherit',
  marginBottom: '0.8rem',
  marginTop: '0',
};

const getFontStyles = ({ theme, cmsTheme, fontType = 'sans' }) => {
  const styles = {
    // Headlines and subheads: Plaid Sans (SITE-6098)
    sans: {
      [CmsThemes.WEB2]: {
        fontFamily: theme.typography.sans,
        color: theme.palette['text/contrast-max'],
      },
      [CmsThemes.WEB3]: {
        fontFamily: theme.typography.plaidSans + ' !important', // bang important to override the global styles
        color: theme.palette['web3/text/contrast-max'],
      },
    },
    // Body copy (P styles): Cern per the design foundations (SITE-6098)
    body: {
      [CmsThemes.WEB2]: {
        fontFamily: theme.typography.sans,
        color: theme.palette['text/contrast-max'],
      },
      [CmsThemes.WEB3]: {
        fontFamily: theme.typography.cern + ' !important', // bang important to override the global styles
        color: theme.palette['web3/text/contrast-max'],
      },
    },
    // Eyebrows: mono on web 2.0, Cern on web 3.0 per the design foundations (SITE-6098)
    mono: {
      [CmsThemes.WEB2]: {
        fontFamily: theme.typography.mono,
        color: theme.palette['text/contrast-max'],
      },
      [CmsThemes.WEB3]: {
        fontFamily: theme.typography.cern + ' !important', // bang important to override the global styles
        color: theme.palette['web3/text/contrast-max'],
      },
    },
  };
  return styles[fontType][cmsTheme];
};

// TODO: [redacted]#####################################
const inlineLinkStyles = ({ theme, cmsTheme }) => {
  // we have to use !important to override the global styles
  const inlineLinkStyles = {
    [CmsThemes.WEB2]: {
      '& a': {
        fontWeight: 600,
        textDecorationColor: `${theme.palette.black600} !important`,
        textDecorationThickness: '1px !important',
        transition: 'text-decoration-color !important',
        transitionDuration: '350ms !important',
        transitionTimingFunction: 'cubic-bezier(0.23, 1.2, 0.32, 1) !important',
        '&:hover, :focus': {
          color: 'inherit !important',
          textDecoration: 'underline !important',
          textDecorationColor: theme.palette.black,
          textDecorationThickness: '2px !important',
          textUnderlineOffset: '4px !important',
        },
        '&:active': {
          textDecorationColor: `${theme.palette.black600} !important`,
          textDecorationThickness: '1px !important',
        },
      },
    },
    [CmsThemes.WEB3]: {
      '& a': {
        color: 'inherit !important',
        backgroundColor: `${theme.palette['web3/component/hyperlink/background-highlight/resting']} !important`,
        textDecorationColor: 'inherit !important',
        fontFamily: 'inherit !important',
        textDecorationThickness: '1px !important',
        display: 'inline-block',
        transition: 'background-color  !important',
        transitionDuration: '350ms !important',
        transitionTimingFunction: 'cubic-bezier(0.23, 1.2, 0.32, 1) !important',
        backgroundBlendMode: 'luminosity !important',
        '&:hover, :focus': {
          backgroundColor: `${theme.palette['web3/component/hyperlink/background-highlight/hovered']} !important`,
          textDecoration: 'none !important',
          backgroundBlendMode: 'luminosity !important',
        },
        '&:active': {
          backgroundColor: `${theme.palette['web3/component/hyperlink/background-highlight/interacted']} !important`,
          backgroundBlendMode: 'luminosity !important',
        },
      },
    },
  };
  return inlineLinkStyles[cmsTheme];
};

const getFontColor = (theme) => {
  return {
    color: theme.palette['web3/text/contrast-max'],
    '& a': {
      color: theme.palette['web3/text/contrast-max'] + ' !important',
      textDecorationColor:
        theme.palette['web3/text/contrast-max'] + ' !important',
    },
  };
};

// Each design-system font style renders as a styled span driven entirely by
// the registry, so new registry styles are available as variants automatically.
const createWeb3FontStyleComponent = (fontStyle: TDesignSystemFontStyle) => {
  return styled('span', {
    label: `${fontStyle.toLowerCase().replace(/ /g, '-')}-w3`,
  })(({ theme }) => {
    return {
      ...getResponsiveCssFontStyles(theme, fontStyle),
      ...getFontColor(theme),
    };
  });
};

const web3FontStyleComponents = Object.fromEntries(
  (Object.keys(DesignSystemFontStyleRegistry) as TDesignSystemFontStyle[]).map(
    (fontStyle) => {
      return [fontStyle, createWeb3FontStyleComponent(fontStyle)];
    },
  ),
) as Record<
  TDesignSystemFontStyle,
  ReturnType<typeof createWeb3FontStyleComponent>
>;

// Rich-text `nodes` configs are untyped, so the compiler can't catch a
// pre-rename variant name arriving from in-flight code; without an alias the
// lookup in Typography falls back to an unstyled span silently. Delete along
// with LegacyDesignSystemFontStyleMap.
const legacyWeb3FontStyleComponents = Object.fromEntries(
  Object.entries(LegacyDesignSystemFontStyleMap).map(
    ([legacyFontStyle, fontStyle]) => {
      return [legacyFontStyle, web3FontStyleComponents[fontStyle]];
    },
  ),
);

export const themes = {
  // bear in mind that these styles are still inheriting from the global styles
  // it's best to inspect the semantic elements and explicitly add the global styles
  // this way when they're removed we will not have any regressions
  // NOTE: that we take a mobile first approach when styling
  'web 3.0': {
    ...web3FontStyleComponents,
    ...legacyWeb3FontStyleComponents,
  },
  // web 2.0 styles
  default: {
    h1: styled('h1', { label: 'h1-w2' })<CmsTheme>(({ theme, cmsTheme }) => {
      return {
        ...getFontStyles({ theme, cmsTheme }),
        fontSize: '3.2rem',
        fontWeight: 600,
        letterSpacing: '-0.15rem',
        lineHeight: '120%',
        marginBottom: '0',
        [theme.breakpoints.up('md')]: {
          fontSize: '6.4rem',
          letterSpacing: '-0.2rem',
          lineHeight: '108%',
        },
      };
    }),
    // Large Headline
    h2: styled('h2', { label: 'h2-w2' })<CmsTheme>(({ theme, cmsTheme }) => {
      return {
        ...getFontStyles({ theme, cmsTheme }),
        fontSize: '3.2rem',
        fontWeight: 600,
        letterSpacing: '-0.2rem',
        lineHeight: '110%',
        marginBottom: '0',
        [theme.breakpoints.up('md')]: {
          fontSize: '6.0rem',
        },
      };
    }),
    // Medium Headline
    h3: styled('h3', { label: 'med-header-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontSize: '2.4rem',
          fontWeight: 600,
          letterSpacing: '-0.2rem',
          lineHeight: '120%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '4.0rem',
          },
        };
      },
    ),
    // Small Headline
    h4: styled('h4', { label: 'sm-header-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontSize: '2.2rem',
          fontWeight: 600,
          letterSpacing: '-0.075rem',
          lineHeight: '128%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '3.0rem',
            letterSpacing: '-0.15rem',
            lineHeight: '120%',
          },
        };
      },
    ),
    // Subhead Regular
    h5: styled('h5', { label: 'subhead-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontSize: '2.0rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '140%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '2.4rem',
          },
          // Subhead SemiBold
          '& b, strong': {
            fontWeight: 600,
          },
        };
      },
    ),
    // P1 Regular
    p: styled('p', { label: 'p1-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'body' }),
          fontSize: '1.8rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '150%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '2.0rem',
          },
          // P1 SemiBold
          '& b, strong': {
            fontWeight: 600,
            fontFamily: 'inherit',
          },
          ...inlineLinkStyles({ theme, cmsTheme }),
        };
      },
    ),
    // P2 Regular
    p2: styled('p', { label: 'p2-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'body' }),
          fontSize: '1.6rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '150%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '1.8rem',
          },
          // P2 SemiBold
          '& b, strong': {
            fontWeight: 600,
            fontFamily: 'inherit',
          },
          ...inlineLinkStyles({ theme, cmsTheme }),
        };
      },
    ),
    // P3 Regular
    p3: styled('p', { label: 'p3-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'body' }),
          fontSize: '1.6rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '150%',
          marginBottom: '0',
          [theme.breakpoints.up('md')]: {
            fontSize: '1.6rem',
          },
          // P3 SemiBold
          '& b, strong': {
            fontWeight: 600,
            fontFamily: 'inherit',
          },
          ...inlineLinkStyles({ theme, cmsTheme }),
        };
      },
    ),
    // P4 Regular
    p4: styled('p', { label: 'p4-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'body' }),
          fontSize: '1.6rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '150%',
          marginBottom: '0',
          [theme.breakpoints.up('sm')]: {
            fontSize: '1.8rem',
          },
          // P4 SemiBold
          '& b, strong': {
            fontWeight: 600,
            fontFamily: 'inherit',
          },
          ...inlineLinkStyles({ theme, cmsTheme }),
        };
      },
    ),
    // P5 Regular
    p5: styled('p', { label: 'p5-regular-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'body' }),
          fontSize: '1.4rem',
          fontWeight: 400,
          letterSpacing: '0rem',
          lineHeight: '150%',
          marginBottom: '0',
          // P5 SemiBold
          '& b, strong': {
            fontWeight: 600,
            fontFamily: 'inherit',
          },
          [theme.breakpoints.up('md')]: {
            fontSize: '1.8rem',
          },
          ...inlineLinkStyles({ theme, cmsTheme }),
        };
      },
    ),
    // EYEBROW
    h6: styled('h6', { label: 'eyebrow-w2' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme, fontType: 'mono' }),
          fontSize: '1.4rem',
          fontWeight: 800,
          letterSpacing: '0.125rem',
          lineHeight: '112%',
          marginBottom: '0',
          textTransform: 'uppercase',
          [theme.breakpoints.up('md')]: {
            fontSize: '1.6rem',
            letterSpacing: '0.2rem',
          },
        };
      },
    ),
  },

  // legacy themes to be deprecated
  'main-menu': {
    h6: styled('h6', { label: 'eyebrow-mm-legacy' })(({ theme }) => {
      return {
        ...DefaultStyles,
        fontSize: '1.6rem',
        fontFamily: theme.typography.mono,
        textTransform: 'uppercase',
        color: colors.black700,
        letterSpacing: '0.15rem',
      };
    }),
    p: styled('p', { label: 'p-mm-legacy' })(({ theme }) => {
      return {
        ...DefaultStyles,
        fontFamily: theme.typography.sans,
        // #ThreadsStrictThemeType
        color: theme.palette['black' as keyof typeof theme.palette],
        fontSize: '1.6rem',
        fontWeight: '600',
        lineHeight: '2.24rem',
      };
    }),
  },
  'main-menu-new': {
    h6: styled('h6', { label: 'h6-mm-new-legacy' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontFamily: theme.typography.sans,
          fontSize: '1.8rem',
          lineHeight: '2.4rem',
          fontWeight: 600,
          letterSpacing: 0,
          textTransform: 'none',
          color: 'inherit',
          [theme.breakpoints.up(1078)]: {
            fontSize: '1.4rem',
            lineHeight: '2rem',
          },
        };
      },
    ),
    p: styled('p', { label: 'p-mm-new-legacy' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontFamily: theme.typography.sans,
          fontSize: '1.8rem',
          lineHeight: 1.333333,
          fontWeight: 600,
          color: 'inherit',
          [theme.breakpoints.up(1078)]: {
            fontSize: '1.6rem',
            lineHeight: 1.5,
          },
        };
      },
    ),
    span: styled('span', { label: 'span-mm-new-legacy' })<CmsTheme>(
      ({ theme, cmsTheme }) => {
        return {
          ...getFontStyles({ theme, cmsTheme }),
          fontFamily: theme.typography.plaidSans,
          fontSize: '1.8rem',
          lineHeight: 1.333333,
          fontWeight: 600,
          color: 'inherit',
          [theme.breakpoints.up(1078)]: {
            fontSize: '1.6rem',
            lineHeight: 1.5,
          },
        };
      },
    ),
  },
};

// TODO: we should be able to remove this once we've migrated everything to web 3.0 paradigms
export const darkOverrides = {
  [CmsThemes.WEB2]: {
    color: 'white',
    '& a': { color: 'white !important' },
  },
  [CmsThemes.WEB3]: {
    color: 'web3/text/contrast-max',
    '& a': {
      color: (theme) => {
        return theme.palette['web3/text/contrast-max'] + ' !important';
      },
      textDecorationColor: (theme) => {
        return theme.palette['web3/text/contrast-max'] + ' !important';
      },
    },
  },
};
